/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { RJSFSchema, UiSchema } from '@rjsf/utils';
import { InputDescription } from '../types/processing-schemas.ts';
import { DateTime } from 'luxon';

export { inlineSubworkflow } from './cwl-pack.ts';

declare global {
  interface HTMLElement {
    exitFullscreen: () => void;
    mozCancelFullScreen: () => void;
    webkitExitFullscreen: () => void;
    msRequestFullscreen: () => void;
    webkitRequestFullscreen: () => void;
  }
  interface Document {
    exitFullscreen: () => Promise<void>;
    mozCancelFullScreen: () => void;
    webkitExitFullscreen: () => void;
  }
}

export const elementToFullscreen = (element: HTMLElement, callback?: () => void) => {
  if (element.requestFullscreen) {
    element.requestFullscreen();
  } else if (element.webkitRequestFullscreen) {
    element.webkitRequestFullscreen();
  } else if (element.msRequestFullscreen) {
    element.msRequestFullscreen();
  }
  if (callback) {
    callback();
  }
};

export const elementExitFullscreen = (element: Document, callback?: () => void) => {
  if (element.exitFullscreen) {
    element.exitFullscreen();
  } else if (element.webkitExitFullscreen) {
    element.webkitExitFullscreen();
  } else if (element.mozCancelFullScreen) {
    element.mozCancelFullScreen();
  }
  if (callback) {
    callback();
  }
};

export const plurals = (length: number, label: string) => {
  if (length > 1) {
    return `${length} ${label}s`;
  } else {
    return `${length} ${label}`;
  }
};

export const getLocaleTimeOffset = (date?: Date): [number, string] => {
  const localDateTime = date ? DateTime.fromJSDate(date) : DateTime.local();
  const { offset, zoneName } = localDateTime;
  return [offset, zoneName];
};

export class Utils {
  static elementToFullscreen(element: HTMLElement, callback?: () => void) {
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen();
    }
    if (callback) {
      callback();
    }
  }

  static elementExitFullscreen(element: Document, callback?: () => void) {
    if (element.exitFullscreen) {
      element.exitFullscreen();
    } else if (element.webkitExitFullscreen) {
      element.webkitExitFullscreen();
    } else if (element.mozCancelFullScreen) {
      element.mozCancelFullScreen();
    }
    if (callback) {
      callback();
    }
  }

  static isAntflowLegacyBbox(description: InputDescription): boolean {
    const isMatchSchema = (description: InputDescription, matches: string[]): boolean => {
      return (
        matches.includes(description.schema.type?.toLowerCase() ?? '') ||
        description.keywords?.some((el) => matches.includes(el.toLowerCase())) ||
        matches.includes(description.title?.toLowerCase() ?? '')
      );
    };
    const bboxMatches = ['bbox', 'bounding_box', 'bounding box', 'boundingbox'];
    return description.schema.type === 'array' && isMatchSchema(description, bboxMatches);
  }

  static injectAddOcMetaData(description: InputDescription, schema: RJSFSchema): RJSFSchema {
    if (this.isAntflowLegacyBbox(description)) {
      const minItems = description.minOccurs || description.schema.minItems || 0;
      return { type: 'array', items: { type: 'number' }, ...schema, minItems: minItems };
    }
    if (description.schema.type === 'boolean' && description.schema.default === undefined) {
      return { ...schema, default: false };
    }
    return schema;
  }

  static isSlider(description: InputDescription): boolean {
    const keys = Object.keys(description.schema);
    return (
      keys.includes('maximum') && keys.includes('minimum') && ['number', 'integer'].includes(description.schema.type)
    );
  }
  static isSecret(description: InputDescription): boolean {
    return description.schema.format === 'secret';
  }

  static injectMatchUISchema(description: InputDescription): UiSchema | undefined {
    if (description.schema.type === 'object' && description.schema.format === 'ogc-bbox') {
      const schema: UiSchema = {};
      Object.entries(description.schema.properties || {}).forEach(([k, v]) => {
        if (k === 'bbox') {
          schema[k] = {
            'ui:widget': 'bboxEditableWidget',
            'ui:description': v.description ?? ''
          };
        }
        if (k == 'crs') {
          schema[k] = {
            'ui:widget': 'hidden'
          };
        }
      });
      return schema;
    }
    if (this.isAntflowLegacyBbox(description)) {
      return {
        'ui:widget': 'bboxEditableWidget',
        'ui:description': description.description
      };
    }
    if (description.schema.contentMediaType) {
      return {
        'ui:options': {
          accept: description.schema.contentMediaType
        }
      };
    }

    if (this.isSlider(description)) {
      return {
        'ui:widget': 'sliderWidget',
        'ui:description': description.description
      };
    }

    if (this.isSecret(description)) {
      return {
        'ui:field': 'secretField'
      };
    }
  }
}
